"use client";

import { useState } from "react";
import { LanguageLink } from "@/components/LanguageLink";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { VideoStatusBadge } from "./VideoStatusBadge";
import { ImageOff, Layers } from "lucide-react";
import type { video } from "@/api/client";
import { useTranslation } from "react-i18next";

interface VideoCardProps extends React.HTMLAttributes<HTMLDivElement> {
  video: video.VideoJob;
}

function VideoCard({ video: job, className, ...props }: VideoCardProps) {
  const [imageError, setImageError] = useState(false);
  const { t } = useTranslation();
  const createdAt = new Date(job.createdAt).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const thumbnailUrl = job.imageUrls?.[0];

  return (
    <LanguageLink to={`/videos/${job.id}`}>
      <Card
        className={cn(
          "bg-white group rounded-md border border-rose-dark shadow-[0_4px_13px_rgba(0,0,0,0.1)] hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden p-0",
          "2k:rounded-lg 4k:rounded-xl",
          className
        )}
        {...props}
      >
        <div className="aspect-video w-full overflow-hidden bg-muted/30 relative">
          {thumbnailUrl && !imageError ? (
            job.mediaTypes?.[0] === "video" ? (
              <div className="relative w-full h-full">
                <video
                  src={thumbnailUrl}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  muted
                  playsInline
                  onLoadedData={(e) => {
                    // Seek to 0.1s to show a frame if it doesn't show by default
                    (e.target as HTMLVideoElement).currentTime = 0.1;
                  }}
                  onError={() => setImageError(true)}
                />
              </div>
            ) : (
              <img
                src={thumbnailUrl}
                alt={job.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={() => setImageError(true)}
              />
            )
          ) : (
            <div className="bg-gray-100 w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageOff className="w-8 h-8 opacity-30" />
            </div>
          )}
          
          {job.jobIds && job.jobIds.length > 1 && (
            <div className={cn(
              "absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1.5 shadow-sm border border-white/20 z-10",
              "2k:text-[18px] 2k:px-2.5 2k:py-1.5 2k:top-3 2k:left-3 2k:gap-2",
              "4k:text-[26px] 4k:px-4 4k:py-2.5 4k:top-5 4k:left-5 4k:gap-3.5",
            )}>
              <Layers className="w-3 h-3 2k:w-5 2k:h-5 4k:w-7 4k:h-7" />
              <span>{t("videoCard.groupCount", { count: job.jobIds.length })}</span>
            </div>
          )}

          <div className="absolute top-2 right-2 2k:top-3 2k:right-3 4k:top-5 4k:right-5">
            <VideoStatusBadge status={job.status} />
          </div>
        </div>

        <CardContent className="p-4 2k:p-5 4k:p-8">
          <div className="flex flex-col gap-1 2k:gap-2.5 4k:gap-3.5">
            <h3 className="font-h3 text-lg truncate group-hover:text-rose-dark transition-colors 2k:text-2xl 4k:text-4xl">
              {job.title}
            </h3>
            {job.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 min-h-10 2k:text-base 2k:min-h-14 4k:text-xl 4k:min-h-20">
                {job.description}
              </p>
            )}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-rose-dark/10 2k:mt-5 2k:pt-5 4k:mt-7 4k:pt-7">
              <p className="text-xs text-muted-foreground 2k:text-lg 4k:text-3xl">
                {t("common.createdAt", { date: createdAt })}
              </p>
              <span className="text-xs font-medium text-rose-dark opacity-0 group-hover:opacity-100 transition-opacity 2k:text-lg 4k:text-2xl">
                {t("videoCard.viewDetails")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </LanguageLink>
  );
}

export { VideoCard };
